<template>
    <div class="leads-kanban">
        <div class="d-flex justify-content-end align-items-center px-3 mb-2">
            <div class="position-relative mr-2">
                <div class="position-absolute notification-badge" v-if="activeFiltersCount">
                    {{ activeFiltersCount }}
                </div>
                <button class="btn btn-filter-section" @click="$bvModal.show('modal-filter')">
                    <div class="d-flex align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="lucide lucide-sliders-horizontal w-4 h-4">
                            <line x1="21" x2="14" y1="4" y2="4"></line>
                            <line x1="10" x2="3" y1="4" y2="4"></line>
                            <line x1="21" x2="12" y1="12" y2="12"></line>
                            <line x1="8" x2="3" y1="12" y2="12"></line>
                            <line x1="21" x2="16" y1="20" y2="20"></line>
                            <line x1="12" x2="3" y1="20" y2="20"></line>
                            <line x1="14" x2="14" y1="2" y2="6"></line>
                            <line x1="8" x2="8" y1="10" y2="14"></line>
                            <line x1="16" x2="16" y1="18" y2="22"></line>
                        </svg>
                        <p class="mb-0 ml-2">{{ $t('recruitingPage.filters') }}</p>
                    </div>
                </button>
            </div>
            <button type="button" class="btn btn-filter-section d-flex align-items-center"
                @click="openColumnsDrawer">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="lucide lucide-columns2">
                    <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                    <path d="M12 3v18"></path>
                </svg>
                <span class="ml-2">{{ $t('recruitingPage.columns') }}</span>
            </button>
        </div>
        <b-sidebar id="kanban-columns-sidebar" sidebar-class="columns-sidebar" :backdrop-variant="'dark'" backdrop shadow
            v-model="showColumns" :right="true" no-header-close>
            <template #header>
                <div class="d-flex align-items-center w-100 justify-content-between">
                    <div class="text-nowrap">{{ $t('recruitingPage.columns') }}</div>
                </div>
            </template>
            <div class="mt-3">
                <draggable v-model="columnDraft" :element="'div'" ghost-class="ghost" @change="commitColumns">
                    <div class="px-4 mb-3 cursor-move" v-for="(col, index) in columnDraft" :key="col.status">
                        <div class="d-flex align-items-center justify-content-between">
                            <b-form-checkbox class="pl-0" :id="`kanban-col-${index}`" v-model="col.visible"
                                @change="onColVisibleChange(col)" @click.native.capture.stop>
                                {{ col.label }}
                            </b-form-checkbox>
                            <svg width="4" height="18" viewBox="0 0 4 18" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <circle cx="2" cy="16" r="2" transform="rotate(-90 2 16)" fill="#C4C4C4" />
                                <circle cx="2" cy="9" r="2" transform="rotate(-90 2 9)" fill="#C4C4C4" />
                                <circle cx="2" cy="2" r="2" transform="rotate(-90 2 2)" fill="#C4C4C4" />
                            </svg>
                        </div>
                        <hr />
                    </div>
                </draggable>
            </div>
            <template #footer>
                <div class="d-flex justify-content-between align-items-center p-3">
                    <button type="button" class="btn mx-0 h-40px w-100px btn-outline-primary"
                        @click="resetColumns">{{ $t('tableColumns.reset') }}</button>
                    <div class="d-flex align-items-center">
                        <b-button class="mx-0 h-40px w-100px" variant="primary"
                            @click="showColumns = false">{{ $t('tableColumns.close') }}</b-button>
                    </div>
                </div>
            </template>
        </b-sidebar>
        <div v-if="isBusy" class="text-center py-5 w-100">
            <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else class="kanban-board">
            <div v-for="col in visibleColumns" :key="col.status" class="kanban-column">
                <div class="kanban-column-header">
                    <span class="kanban-column-title">{{ col.label }}</span>
                    <span class="kanban-column-count">{{ cols[col.status].total }}</span>
                </div>
                <div class="kanban-column-body" :data-status="col.status" ref="columnBodies"
                    @scroll="onColumnScroll(col.status, $event)">
                    <div v-for="lead in cols[col.status].items" :key="lead.id" class="kanban-card" :data-id="lead.id"
                        >
                        <p class="kanban-card-name mb-1 cursor-pointer" @click="$emit('openLead', lead.id)">{{ lead.name }}</p>
                        <p v-if="lead.job && lead.job !== '-'" class="kanban-card-meta mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor"
                                viewBox="0 0 16 16" class="mr-1 kanban-card-icon">
                                <path
                                    d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5zm1.886 6.914L15 7.151V12.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V7.15l6.614 1.764a1.5 1.5 0 0 0 .772 0zM1.5 4h13a.5.5 0 0 1 .5.5v1.616L8.129 7.948a.5.5 0 0 1-.258 0L1 6.116V4.5a.5.5 0 0 1 .5-.5z" />
                            </svg>{{ lead.job }}
                        </p>
                        <p class="kanban-card-meta mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor"
                                viewBox="0 0 16 16" class="mr-1 kanban-card-icon">
                                <path
                                    d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
                            </svg>{{ lead.date }}
                        </p>
                        <p class="kanban-card-meta mb-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor"
                                viewBox="0 0 16 16" class="mr-1 kanban-card-icon">
                                <path
                                    d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                            </svg>{{ lead.owner }}
                        </p>
                        <p class="kanban-card-note mb-0 cursor-pointer" @click="$emit('openNote', lead.id)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor"
                                viewBox="0 0 16 16" class="mr-1 kanban-card-icon">
                                <path
                                    d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-2.5a2 2 0 0 0-1.6.8L8 14.333 6.1 11.8a2 2 0 0 0-1.6-.8H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2.5a1 1 0 0 1 .8.4l1.9 2.533a1 1 0 0 0 1.6 0l1.9-2.533a1 1 0 0 1 .8-.4H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z" />
                            </svg>{{ lead.note }}
                        </p>
                        <div v-if="lead.lastActivityRelative" class="kanban-card-footer">
                            <span class="kanban-card-activity" :title="lead.lastActivity"
                                :style="{ color: lead.lastActivityColor }">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor"
                                    viewBox="0 0 16 16" class="mr-1 kanban-card-icon">
                                    <path
                                        d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z" />
                                    <path
                                        d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z" />
                                </svg>{{ lead.lastActivityRelative }}
                            </span>
                        </div>
                    </div>
                    <div v-if="cols[col.status].loading" class="kanban-loading">
                        <b-spinner small variant="primary"></b-spinner>
                    </div>
                    <div v-else-if="!cols[col.status].items.length" class="kanban-empty">
                        {{ $t('recruitingPage.emptyPosition') }}
                    </div>
                </div>
            </div>
        </div>
        <DeleteModal :message="$t('recruitingDetails.hiredWarning')" :updateMessage="true" :removeMessage="''"
            v-if="showHiredStatusWarning" @confirm="verifyDriverStatus" />
    </div>
</template>
<script>
import * as moment from "moment-timezone";
import { mapActions } from "vuex";
import draggable from 'vuedraggable';
import DeleteModal from "@/components/DeleteModal.vue";

const PAGE_SIZE = 20;

export default {
    name: "LeadsKanban",
    components: { draggable, DeleteModal },
    props: {
        search: { type: String, default: '' },
        owner: { type: [Number, String], default: '' },
        job: { type: [Number, String], default: '' },
        refreshKey: { type: [Number, String], default: 0 },
        tab: { type: String, default: 'lead,applicant,hired,terminated' },
    },
    data() {
        return {
            isBusy: false,
            cols: {},
            sortables: [],
            hiddenColumns: [],
            columnOrder: [],
            columnDraft: [],
            showColumns: false,
            showHiredStatusWarning: false,
            pendingMove: null,
        };
    },
    computed: {
        orderedColumns() {
            // Apply the saved custom order, dropping any statuses that no longer exist.
            if (!this.columnOrder.length) return this.columns;
            const saved = new Set(this.columnOrder);
            const ordered = this.columnOrder
                .map((status) => this.columns.find((c) => c.status === status))
                .filter(Boolean);
            // Columns added since the order was saved slot into their default
            // position rather than getting appended at the end.
            this.columns.forEach((c, defaultIndex) => {
                if (!saved.has(c.status)) {
                    ordered.splice(Math.min(defaultIndex, ordered.length), 0, c);
                }
            });
            return ordered;
        },
        visibleColumns() {
            return this.orderedColumns.filter((c) => !this.hiddenColumns.includes(c.status));
        },
        visibleStatuses() {
            return this.visibleColumns.map((c) => c.status);
        },
        activeFiltersCount() {
            return (this.owner ? 1 : 0) + (this.job ? 1 : 0);
        },
        columns() {
            // One combined board spanning every stage across all three tabs.
            return [
                { status: 'New Lead', label: this.$t('recruitingPage.statusNewLead'), colorClass: 'is-warning' },
                { status: 'Contacted', label: this.$t('recruitingPage.statusContacted'), colorClass: 'is-primary' },
                { status: 'Follow Up', label: this.$t('recruitingPage.statusFollowUp'), colorClass: 'is-info' },
                { status: 'Applied', label: this.$t('recruitingPage.statusApplied'), colorClass: 'is-warning' },
                { status: 'Under Review', label: this.$t('recruitingPage.statusUnderReview'), colorClass: 'is-primary' },
                { status: 'Screening', label: this.$t('recruitingPage.statusScreening'), colorClass: 'is-success' },
                { status: 'Onboarding', label: this.$t('recruitingPage.statusOnboarding'), colorClass: 'is-success' },
                { status: 'Lead Closed', label: this.$t('recruitingPage.statusLeadClosed'), colorClass: 'is-secondary' },
                { status: 'Rejected', label: this.$t('recruitingPage.statusRejected'), colorClass: 'is-danger' },
                { status: 'Hired', label: this.$t('recruitingPage.tabHired'), colorClass: 'is-success' },
                { status: 'Terminated', label: this.$t('recruitingPage.tabTerminated'), colorClass: 'is-danger' },
            ];
        },
    },
    methods: {
        ...mapActions(["getRecruitingData", "updateRecruitingItem", "setNotification", "setLoader"]),
        initColState() {
            const cols = {};
            this.columns.forEach((c) => {
                cols[c.status] = { items: [], page: 1, total: 0, loading: false };
            });
            this.cols = cols;
        },
        loadColumnVisibility() {
            try {
                const saved = JSON.parse(localStorage.getItem('recruitingKanbanHiddenColumns') || '[]');
                if (Array.isArray(saved)) this.hiddenColumns = saved;
            } catch (e) {
                this.hiddenColumns = [];
            }
            try {
                const order = JSON.parse(localStorage.getItem('recruitingKanbanColumnOrder') || '[]');
                if (Array.isArray(order)) this.columnOrder = order;
            } catch (e) {
                this.columnOrder = [];
            }
        },
        openColumnsDrawer() {
            // Build an editable working copy from the current order + visibility.
            this.columnDraft = this.orderedColumns.map((c) => ({
                status: c.status,
                label: c.label,
                visible: !this.hiddenColumns.includes(c.status),
            }));
            this.showColumns = true;
        },
        commitColumns() {
            // Apply the working copy (order + visibility) to the board instantly.
            this.columnOrder = this.columnDraft.map((c) => c.status);
            this.hiddenColumns = this.columnDraft.filter((c) => !c.visible).map((c) => c.status);
            localStorage.setItem('recruitingKanbanColumnOrder', JSON.stringify(this.columnOrder));
            localStorage.setItem('recruitingKanbanHiddenColumns', JSON.stringify(this.hiddenColumns));
            // Column bodies changed — rebind Sortable to the new set.
            this.$nextTick(this.initSortable);
        },
        onColVisibleChange(col) {
            // Keep at least one column visible — revert the toggle if it was the last.
            if (!this.columnDraft.some((c) => c.visible)) {
                col.visible = true;
                this.setNotification({
                    msg: this.$t('recruitingPage.keepOneColumn'),
                    type: 'error',
                    color: 'red',
                });
                return;
            }
            this.commitColumns();
        },
        resetColumns() {
            // Restore the default order with every column visible, applied instantly.
            this.columnDraft = this.columns.map((c) => ({
                status: c.status,
                label: c.label,
                visible: true,
            }));
            this.commitColumns();
        },
        htmlToPlainText(htmlString) {
            if (!htmlString) return '';
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlString;
            return tempDiv.textContent || tempDiv.innerText || '';
        },
        mapCard(item) {
            return {
                id: item.id,
                name: item.name,
                status: item.status,
                job: item.job ? item.job.name : '-',
                owner: item.owner ? item.owner.name : '-',
                date: item.date ? moment(item.date).format('ll') : '-',
                lastActivity: item.last_activity ? moment(item.last_activity).format('lll') : '-',
                lastActivityRelative: this.relativeActivity(item.last_activity),
                lastActivityColor: this.activityColor(item.last_activity),
                note: item.last_note ? this.htmlToPlainText(item.last_note.note) : '-',
            };
        },
        activityColor(dateStr) {
            if (!dateStr) return '#000000';
            const then = moment(dateStr);
            if (!then.isValid()) return '#000000';
            const days = moment().startOf('day').diff(then.clone().startOf('day'), 'days');
            if (days >= 14) return '#FF0000'; // 14+ days old
            if (days >= 7) return '#FFC800'; // 7-13 days old
            return '#6B7280';
        },
        relativeActivity(dateStr) {
            if (!dateStr) return '';
            const then = moment(dateStr);
            if (!then.isValid()) return '';
            const now = moment();
            const mins = now.diff(then, 'minutes');
            if (mins < 1) return 'now';
            if (mins < 60) return `${mins}m`;
            const hrs = now.diff(then, 'hours');
            if (hrs < 24) return `${hrs}h`;
            const days = now.diff(then, 'days');
            if (days < 30) return `${days}d`;
            const months = now.diff(then, 'months');
            if (months < 12) return `${months}mo`;
            return `${now.diff(then, 'years')}y`;
        },
        hasMore(status) {
            const col = this.cols[status];
            return col && col.items.length < col.total;
        },
        async loadColumn(status) {
            const col = this.cols[status];
            if (!col || col.loading) return;
            // Nothing more to fetch (but always allow the very first page).
            if (col.page > 1 && !this.hasMore(status)) return;
            col.loading = true;
            try {
                let url = "";
                if (this.search) url += `search=${this.search}&`;
                if (this.owner) url += `owner=${this.owner}&`;
                if (this.job) url += `job=${this.job}&`;
                url += `status=${encodeURIComponent(status)}&page=${col.page}&page_size=${PAGE_SIZE}&tab=${this.tab}`;
                const data = await this.getRecruitingData({ type: this.tab, url });
                (data.data || []).forEach((item) => col.items.push(this.mapCard(item)));
                col.total = data.total_entries_count || 0;
                col.page += 1;
            } catch (e) {
                console.log(e);
            } finally {
                col.loading = false;
            }
        },
        async reloadBoard() {
            this.isBusy = true;
            this.initColState();
            // Render the empty board first so column bodies exist, then fill each column.
            this.isBusy = false;
            await this.$nextTick();
            await Promise.all(this.columns.map((c) => this.loadColumn(c.status)));
            this.$nextTick(this.initSortable);
        },
        onColumnScroll(status, evt) {
            const el = evt.target;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
                if (this.hasMore(status)) this.loadColumn(status);
            }
        },
        destroySortable() {
            this.sortables.forEach((s) => { try { s.destroy(); } catch (e) { /* noop */ } });
            this.sortables = [];
        },
        initSortable() {
            if (!window.Sortable) return;
            this.destroySortable();
            const bodies = this.$refs.columnBodies || [];
            bodies.forEach((el) => {
                this.sortables.push(window.Sortable.create(el, {
                    group: 'leads-kanban',
                    animation: 150,
                    ghostClass: 'kanban-card-ghost',
                    onEnd: (evt) => this.onCardMoved(evt),
                }));
            });
        },
        // Undo Sortable's DOM mutation so Vue's data stays the single source of truth.
        undoSortableDom(evt) {
            if (evt.item.parentNode) evt.item.parentNode.removeChild(evt.item);
            const ref = evt.from.children[evt.oldIndex] || null;
            evt.from.insertBefore(evt.item, ref);
        },
        async onCardMoved(evt) {
            const fromStatus = evt.from.dataset.status;
            const toStatus = evt.to.dataset.status;
            const id = Number(evt.item.dataset.id);
            // Put the DOM back where it was; the data model drives the actual move.
            this.undoSortableDom(evt);
            if (fromStatus === toStatus) return;

            const fromCol = this.cols[fromStatus];
            const toCol = this.cols[toStatus];
            const idx = fromCol.items.findIndex((c) => c.id === id);
            if (idx === -1) return;
            const card = fromCol.items[idx];

            // Optimistic move in local state (no refetch).
            fromCol.items.splice(idx, 1);
            card.status = toStatus;
            toCol.items.splice(evt.newIndex, 0, card);
            fromCol.total = Math.max(0, fromCol.total - 1);
            toCol.total += 1;

            // Remember the move so it can be committed or reverted later.
            this.pendingMove = { id, fromStatus, toStatus, idx, card };

            // Moving to Hired is irreversible — confirm before committing.
            if (toStatus === 'Hired') {
                this.showHiredStatusWarning = true;
                return;
            }
            await this.commitStatusChange();
        },
        async verifyDriverStatus(confirmed) {
            this.showHiredStatusWarning = false;
            if (confirmed) {
                await this.commitStatusChange();
                // Force a refresh so the board reflects the confirmed Hired move.
                this.reloadBoard();
            } else {
                this.revertPendingMove();
            }
        },
        async commitStatusChange() {
            const move = this.pendingMove;
            if (!move) return;
            try {
                this.setLoader(true);
                await this.updateRecruitingItem({ id: move.id, payload: { status: move.toStatus } });
                await this.setNotification({
                    msg: this.$t('recruitingPage.statusUpdated'),
                    type: "success",
                    color: "green",
                });
                this.setLoader(false);
                this.pendingMove = null;
                this.$emit("updated");
            } catch (e) {
                this.setLoader(false);
                console.log(e);
                this.revertPendingMove();
                await this.setNotification({
                    msg: typeof e === 'object' ? e.error : this.$t('recruitingPage.statusUpdateFailed'),
                    type: "error",
                    color: "red",
                });
            }
        },
        revertPendingMove() {
            const move = this.pendingMove;
            if (!move) return;
            const { id, fromStatus, toStatus, idx, card } = move;
            const fromCol = this.cols[fromStatus];
            const toCol = this.cols[toStatus];
            const backIdx = toCol.items.findIndex((c) => c.id === id);
            if (backIdx > -1) toCol.items.splice(backIdx, 1);
            card.status = fromStatus;
            fromCol.items.splice(idx, 0, card);
            fromCol.total += 1;
            toCol.total = Math.max(0, toCol.total - 1);
            this.pendingMove = null;
        },
    },
    watch: {
        refreshKey() {
            this.reloadBoard();
        },
        search() {
            this.reloadBoard();
        },
        owner() {
            this.reloadBoard();
        },
        job() {
            this.reloadBoard();
        },
        tab() {
            this.reloadBoard();
        },
    },
    created() {
        this.loadColumnVisibility();
        this.initColState();
    },
    mounted() {
        this.reloadBoard();
    },
    beforeDestroy() {
        this.destroySortable();
    },
};
</script>
<style>
.b-sidebar-footer{
    background-color: white !important;
    border-top: 1px solid #dee2e6 !important;
}
</style>
<style scoped>
.kanban-board {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 4px 16px 16px;
    align-items: flex-start;
}

.kanban-column {
    flex: 0 0 260px;
    max-width: 260px;
    background: #f5f6f8;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 220px);
}

.kanban-column-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 5px 5px 0 0;
    font-weight: 600;
    font-size: 13px;
}

.kanban-column-header.is-warning { border-top-color: #ffc107; }
.kanban-column-header.is-primary { border-top-color: #2d69f6; }
.kanban-column-header.is-success { border-top-color: #2cce89; }
.kanban-column-header.is-danger { border-top-color: #ff5b5b; }
.kanban-column-header.is-secondary { border-top-color: #8290ac; }

.kanban-column-title { color: #222736; }

.kanban-column-count {
    background: #ffffff;
    border-radius: 12px;
    padding: 0 8px;
    font-size: 12px;
    color: #6b7280;
    min-width: 24px;
    text-align: center;
}

.kanban-column-body {
    padding: 10px;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 60px;
}

.kanban-card {
    background: #ffffff;
    border: 1px solid #e2e6f0;
    border-radius: 5px;
    padding: 10px 12px;
    margin-bottom: 8px;
    transition: box-shadow 0.15s ease;
}

.kanban-card:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); }

.kanban-card-name {
    font-weight: 600;
    font-size: 14px;
    color: #2d69f6;
    word-break: break-word;
    text-align: left;
}

.kanban-card-meta {
    font-size: 12px;
    color: #8290ac;
    word-break: break-word;
    text-align: left;
}

.kanban-card-icon {
    vertical-align: -1px;
    flex-shrink: 0;
}

.kanban-card-space {
    height: 10px;
}

.kanban-card-note {
    font-size: 12px;
    color: #6b7280;
    word-break: break-word;
    text-align: left;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.kanban-card-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 8px;
}

.kanban-card-activity {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: #8290ac;
    white-space: nowrap;
}

.kanban-card-ghost {
    opacity: 0.5;
    background: #eef3ff;
}

.kanban-empty {
    text-align: center;
    color: #adb5bd;
    font-size: 12px;
    padding: 16px 4px;
}

.kanban-loading {
    text-align: center;
    padding: 12px 4px;
}
</style>
